import { Badge, Box, Group, Text, Tooltip } from '@mantine/core';
import type { CSSProperties } from 'react';
import {
  clockTime, eventEmoji, formatFullDateTime, severityBadgeVariant, severityDotColor, type TimelineEventView,
} from './timelineEvent';

/** Shared card frame for Timeline cards and group headers. */
export const timelineCardFrame: CSSProperties = {
  borderRadius: 'var(--mantine-radius-md)',
  border: '1px solid var(--mantine-color-default-border)',
  backgroundColor: 'var(--mantine-color-body)',
  cursor: 'pointer',
  transition: 'color 150ms, background-color 150ms, border-color 150ms',
};

/** Title typography for Timeline cards and group headers, spelled out so it
 *  no longer depends on the host app's global `h3` styles. The values are the
 *  admin app's computed `h3` (browser default 1.17em of a 16px body, Mantine's
 *  1.55 body line-height, 1em block margins), so the admin renders unchanged. */
export const TIMELINE_TITLE_FZ = 18.72;
export const timelineTitleStyle: CSSProperties = {
  margin: `${TIMELINE_TITLE_FZ}px 0`,
  minWidth: 0,
};

export interface TimelineEventCardProps<V extends TimelineEventView = TimelineEventView> {
  event: V;
  /** Called with the same view-model object when the card is clicked. */
  onClick?: (event: V) => void;
}

/** One ungrouped event on the Timeline: severity dot, emoji + title + severity
 *  badge, optional description, then time · actor · device · location. */
export function TimelineEventCard<V extends TimelineEventView = TimelineEventView>({
  event,
  onClick,
}: TimelineEventCardProps<V>) {
  return (
    <Box p={16} style={timelineCardFrame} onClick={() => onClick?.(event)} data-event-id={event.id}>
      <Group align="flex-start" gap={16} wrap="nowrap">
        <Box w={12} h={12} mt={8} style={{ borderRadius: 9999, flexShrink: 0, backgroundColor: severityDotColor(event.severity) }} />
        <Box miw={0} flex={1}>
          <Group mb="xs" gap={8} wrap="nowrap">
            <Text component="span" fz="sm">{eventEmoji(event.kind, event.title)}</Text>
            <Text
              component="h3"
              data-timeline-title
              title={event.title}
              fz={`${TIMELINE_TITLE_FZ}px`}
              fw={500}
              lh={1.55}
              truncate="end"
              style={timelineTitleStyle}
            >
              {event.title}
            </Text>
            <Badge variant={severityBadgeVariant(event.severity)} size="sm" style={{ flexShrink: 0 }}>
              {event.severity}
            </Badge>
          </Group>
          {event.description && (
            <Text fz="sm" c="dimmed" mb="sm">
              {event.description}
            </Text>
          )}
          <Group fz="xs" c="dimmed" gap={16} wrap="wrap">
            <Tooltip label={formatFullDateTime(event.time)} withArrow>
              <span>{clockTime(event.time)}</span>
            </Tooltip>
            {event.actorName && <span>👤 {event.actorName}</span>}
            {/* WHERE it happened — device first (most specific), location as fallback/extra */}
            {event.deviceName && <span>📍 {event.deviceName}</span>}
            {event.location && <span>{event.deviceName ? '· ' : '📍 '}{event.location}</span>}
          </Group>
        </Box>
      </Group>
    </Box>
  );
}
